import { useState, useRef, useCallback } from "react";
import { useDispatchApp } from "../../../../lib/redux";
import { startEditImage } from "../../../../actions/user";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  MODAL_IMAGE_EDIT_PROCESSING_MESSAGE,
  MODAL_IMAGE_EDIT_EDITED_IMAGE_LABEL,
} from "../../../../i18n/keysTranslations";
import {
  PROMPTS_CHECKPOINT_SIZE,
  mergeAttachmentUrls,
  mergeAttachmentFileNames,
} from "./imageEditCheckpoint";

export { PROMPTS_CHECKPOINT_SIZE };

/**
 * Custom hook to handle image editing logic
 */
export const useImageEdit = () => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [versionCounter, setVersionCounter] = useState(0);

  const accumulatedAttachmentUrlsRef = useRef([]);
  const accumulatedAttachmentFileNamesRef = useRef({});

  const resetAttachmentAccumulation = useCallback(() => {
    accumulatedAttachmentUrlsRef.current = [];
    accumulatedAttachmentFileNamesRef.current = {};
  }, []);

  /**
   * Edits an image with a prompt
   */
  const editImage = async (
    url,
    promptsOrdered,
    businessLookAndFeel,
    attachmentUrls = [],
    attachmentFileNames = {},
    onSuccess,
    onError
  ) => {
    if (isProcessing || !url || !promptsOrdered.length) return;

    setIsProcessing(true);
    setIsEditing(true);

    const mergedAttachmentUrls = mergeAttachmentUrls(
      accumulatedAttachmentUrlsRef.current,
      attachmentUrls
    );
    const mergedAttachmentFileNames = mergeAttachmentFileNames(
      accumulatedAttachmentFileNamesRef.current,
      attachmentFileNames
    );

    try {
      const response = await dispatch(
        startEditImage({
          url,
          prompts: promptsOrdered,
          businessLookAndFeel: businessLookAndFeel || "",
          attachmentUrls: mergedAttachmentUrls,
        })
      );

      if (response?.ok && response?.data?.url) {
        accumulatedAttachmentUrlsRef.current = mergedAttachmentUrls;
        accumulatedAttachmentFileNamesRef.current = mergedAttachmentFileNames;

        const newVersion = versionCounter + 1;
        setVersionCounter((prev) => prev + 1);

        const aiResponse = {
          id: (Date.now() + 2).toString(),
          role: "assistant",
          content: t(MODAL_IMAGE_EDIT_EDITED_IMAGE_LABEL, {
            version: newVersion,
          }),
          timestamp: new Date(),
          version: newVersion,
          imageUrl: response.data.url,
        };

        onSuccess(response.data.url, aiResponse);
      } else {
        const errorText = response?.message || "";
        throw Object.assign(new Error(errorText), {
          code: response?.code,
          provider: response?.provider,
          paymentMode: response?.paymentMode,
          message: errorText,
        });
      }
    } catch (error) {
      console.error("Error editing image:", error);
      const errorText =
        typeof error?.message === "string" ? error.message.trim() : "";
      const errorMessage = {
        id: (Date.now() + 2).toString(),
        role: "assistant",
        content: `Error: ${errorText}`,
        timestamp: new Date(),
        isError: true,
      };

      onError(errorMessage);
    } finally {
      setIsProcessing(false);
      setIsEditing(false);
    }
  };

  /**
   * Creates a thinking message
   */
  const createThinkingMessage = () => ({
    id: (Date.now() + 1).toString(),
    role: "assistant",
    content: t(MODAL_IMAGE_EDIT_PROCESSING_MESSAGE),
    timestamp: new Date(),
    isThinking: true,
  });

  return {
    isProcessing,
    isEditing,
    versionCounter,
    editImage,
    createThinkingMessage,
    resetAttachmentAccumulation,
  };
};
